// Centralized API client for the Profit & Margin pages.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/profit`;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// Raised when a report the page needs has not finished syncing. Carries the
// backend's explanation so the page can state the reason rather than fail blankly.
export class ReportUnavailableError extends Error {
  constructor(message) {
    super(message);
    this.reportUnavailable = true;
  }
}

// Raised when no Amazon account is connected, which is a different fix entirely.
export class NotConnectedError extends Error {
  constructor(message) {
    super(message);
    this.notConnected = true;
  }
}

const call = async (url, options = {}) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.error === 'report_unavailable') {
    throw new ReportUnavailableError(data.message);
  }
  if (res.status === 400 && data.error === 'amazon_not_connected') {
    throw new NotConnectedError(data.message);
  }
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
};

const range = ({ start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return query ? `?${query}` : '';
};

// ── Page 1 — Profit Dashboard ────────────────────────────────────────────────

export const getDashboard = (token, window) =>
  call(`${BASE}/dashboard${range(window)}`, { headers: authHeaders(token) });

export const getSkus = (token, window) =>
  call(`${BASE}/skus${range(window)}`, { headers: authHeaders(token) });

export const getPriceBands = (token, sku, window) =>
  call(`${BASE}/skus/${encodeURIComponent(sku)}/price-bands${range(window)}`,
    { headers: authHeaders(token) });

export const getOrderItems = (token, window, page = 1, perPage = 50) => {
  const params = new URLSearchParams({ page, per_page: perPage });
  if (window?.start) params.set('start', window.start);
  if (window?.end) params.set('end', window.end);
  return call(`${BASE}/order-items?${params}`, { headers: authHeaders(token) });
};

export const getTrend = (token, window, bucket = 'MONTH') => {
  const params = new URLSearchParams({ bucket });
  if (window?.start) params.set('start', window.start);
  if (window?.end) params.set('end', window.end);
  return call(`${BASE}/trend?${params}`, { headers: authHeaders(token) });
};

// Dismisses a finding the seller judged intentional, or restores it.
export const dismissFinding = (token, sku, code) =>
  call(`${BASE}/findings/dismissed`, {
    method: 'POST', headers: authHeaders(token), body: JSON.stringify({ sku, code }),
  });

export const restoreFinding = (token, sku, code) =>
  call(`${BASE}/findings/dismissed`, {
    method: 'DELETE', headers: authHeaders(token), body: JSON.stringify({ sku, code }),
  });

// ── Page 2 — Cost Setup ──────────────────────────────────────────────────────

export const getCosts = (token, window) =>
  call(`${BASE}/costs${range(window)}`, { headers: authHeaders(token) });

// Adds a cost revision — earlier periods keep the batch that was in force.
export const saveCost = (token, sku, body) =>
  call(`${BASE}/costs/${encodeURIComponent(sku)}`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify(body),
  });

// Corrects an existing revision, rewriting history from its effective date.
export const updateBatch = (token, batchId, changes) =>
  call(`${BASE}/costs/batch/${batchId}`, {
    method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(changes),
  });

export const deleteBatch = (token, batchId) =>
  call(`${BASE}/costs/batch/${batchId}`, {
    method: 'DELETE', headers: authHeaders(token),
  });

export const getSettings = (token) =>
  call(`${BASE}/settings`, { headers: authHeaders(token) });

export const saveSettings = (token, changes) =>
  call(`${BASE}/settings`, {
    method: 'PUT', headers: authHeaders(token), body: JSON.stringify(changes),
  });

// Accepts .csv, .xlsx or .xls — sellers keep costs in spreadsheets, and asking
// them to convert first is what leaves a catalogue permanently half-costed.
export const importCosts = async (token, file) => {
  const body = new FormData();
  body.append('file', file);
  // No Content-Type header: the browser sets the multipart boundary itself.
  return call(`${BASE}/costs/import`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
  });
};

export const exportCostsUrl = () => `${BASE}/costs/export`;

// Export needs the bearer token, so it is fetched and saved rather than linked.
export const downloadCosts = async (token) => {
  const res = await fetch(exportCostsUrl(), { headers: authHeaders(token) });
  if (!res.ok) throw new Error('Could not export costs.');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'sku-costs.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
